import React, { useState, useMemo } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card       from "@react/shared/ui/Card"
import EmptyState from "@react/shared/ui/EmptyState"

const fmt = m => (m && m.formatted) || "—"
const GRID = "40px 140px minmax(220px,1fr) 80px 130px 130px 130px 80px"

const FavoriteToggle = ({ line, togglePath }) => {
  const [favorite, setFavorite] = useState(line.favorite)
  const [saving,   setSaving]   = useState(false)

  const toggle = async () => {
    const previous = favorite
    const next     = !previous
    setFavorite(next)
    setSaving(true)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res  = await fetch(togglePath, {
        method:  "PATCH",
        headers: {
          "X-CSRF-Token":     csrf,
          "Accept":           "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
      if (!res.ok) setFavorite(previous)
    } catch {
      setFavorite(previous)
    } finally {
      setSaving(false)
    }
  }

  return (
    <button
      type      = "button"
      onClick   = {toggle}
      disabled  = {saving}
      className = "inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-amber-50 transition"
      title     = {I18n.t("mbc_contracts.mbc_lines.columns.favorite", { defaultValue: "Favori" })}
    >
      <FontAwesomeIcon
        icon      = "star"
        className = {`text-[15px] transition ${favorite ? "text-amber-400" : "text-slate-300"}`}
      />
    </button>
  )
}

const LineRow = ({ line, paths, policies }) => {
  const togglePath = paths.toggle_pattern.replace(":id", line.id)
  const editPath   = paths.edit_pattern.replace(":id", line.id)
  const destroyPath = paths.destroy_pattern.replace(":id", line.id)
  return (
    <div
      className = "grid items-center gap-x-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
      style     = {{ gridTemplateColumns: GRID }}
    >
      <div className="flex items-center justify-center">
        <FavoriteToggle line={line} togglePath={togglePath} />
      </div>
      <div className="text-sm font-mono text-slate-800 tabular-nums truncate">{line.code}</div>
      <div className="text-sm text-slate-800 truncate" title={line.label}>{line.label}</div>
      <div className="text-center text-xs text-slate-500">{line.unit}</div>
      <div className="text-right text-sm text-slate-700 tabular-nums">{fmt(line.purchase_price)}</div>
      <div className="text-right text-sm text-slate-900 tabular-nums font-medium">{fmt(line.unit_price)}</div>
      <div className={`text-right text-sm tabular-nums font-medium ${line.margin_positive ? "text-emerald-600" : "text-rose-600"}`}>
        {fmt(line.margin)}
      </div>
      <div className="flex items-center justify-end gap-1">
        {policies.can_edit && (
          <a
            href      = {editPath}
            className = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            title     = {I18n.t("button.edit", { defaultValue: "Modifier" })}
          >
            <FontAwesomeIcon icon="pen" className="text-[12px]" />
          </a>
        )}
        {policies.can_destroy && (
          <a
            href         = {destroyPath}
            data-method  = "delete"
            data-confirm = {I18n.t("button.sure", { defaultValue: "Êtes-vous sûr ?" })}
            className    = "inline-flex items-center justify-center w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700"
            title        = {I18n.t("button.delete", { defaultValue: "Supprimer" })}
          >
            <FontAwesomeIcon icon="trash" className="text-[12px]" />
          </a>
        )}
      </div>
    </div>
  )
}

const HeaderRow = () => (
  <div
    className = "grid items-center gap-x-3 px-4 py-3 border-b border-slate-200 bg-slate-50/50 uppercase tracking-wider text-[11px] font-medium text-slate-500"
    style     = {{ gridTemplateColumns: GRID }}
  >
    <div className="text-center">{I18n.t("mbc_contracts.mbc_lines.columns.favorite", { defaultValue: "Fav." })}</div>
    <div>{I18n.t("mbc_contracts.mbc_lines.columns.ref")}</div>
    <div>{I18n.t("mbc_contracts.mbc_lines.columns.label")}</div>
    <div className="text-center">{I18n.t("mbc_contracts.mbc_lines.columns.unit")}</div>
    <div className="text-right">{I18n.t("mbc_contracts.mbc_lines.columns.cost")}</div>
    <div className="text-right">{I18n.t("mbc_contracts.mbc_lines.columns.sale_price")}</div>
    <div className="text-right">{I18n.t("mbc_contracts.mbc_lines.columns.margin")}</div>
    <div></div>
  </div>
)

const App = ({ data }) => {
  if (!data) return null
  const [search, setSearch] = useState("")
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return data.lines
    return data.lines.filter(l =>
      (l.code  && l.code.toLowerCase().includes(q)) ||
      (l.label && l.label.toLowerCase().includes(q))
    )
  }, [data.lines, search])

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="px-8 py-8">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <a
              href      = {data.paths.back}
              className = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              title     = {I18n.t("button.back", { defaultValue: "Retour" })}
            >
              <FontAwesomeIcon icon="arrow-left" className="text-[13px]" />
            </a>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-slate-900 truncate">
                {I18n.t("mbc_contracts.mbc_lines.title")}
              </h1>
              <span className="text-sm text-slate-500">
                {data.lines.length} {data.lines.length === 1 ? "ligne" : "lignes"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {data.policies.can_create && data.paths.new_line && (
              <a
                href      = {data.paths.new_line}
                className = "inline-flex items-center gap-2 h-9 px-3 rounded-md bg-brand-500 text-[13px] font-medium text-white hover:bg-brand-600"
              >
                <FontAwesomeIcon icon="plus" className="text-[12px]" />
                {I18n.t("mbc_contracts.mbc_lines.add", { defaultValue: "Ajouter une ligne" })}
              </a>
            )}
          </div>
        </div>

        <Card className="rounded-lg">
          <div className="flex items-center gap-4 px-4 py-3 border-b border-slate-200">
            <div className="relative flex-shrink-0 w-72">
              <FontAwesomeIcon
                icon      = "magnifying-glass"
                className = "absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[12px]"
              />
              <input
                type        = "text"
                value       = {search}
                onChange    = {e => setSearch(e.target.value)}
                placeholder = {I18n.t("workorders.filter_button", { defaultValue: "Rechercher" })}
                className   = "w-full h-9 pl-9 pr-3 bg-white border border-slate-200 rounded-md text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
              />
            </div>
          </div>

          <HeaderRow />
          {filtered.length === 0 ? (
            <EmptyState size="lg">
              {I18n.t("mbc_contracts.mbc_lines.none", { defaultValue: "Aucune ligne" })}
            </EmptyState>
          ) : (
            filtered.map(line => (
              <LineRow key={line.id} line={line} paths={data.paths} policies={data.policies} />
            ))
          )}
        </Card>
      </div>
    </div>
  )
}

export default App
